/**
 * Error State Service
 *
 * Element-scoped signal-based state management for authorization errors.
 * Tracks 401/403 responses and provides reactive state for UI rendering.
 *
 * This service is intentionally NOT provided in 'root' to ensure each
 * <fga-manager> instance has isolated error state (guides/02-signal-state-web-components.md).
 *
 * @packageDocumentation
 */

import { Injectable, signal, computed } from '@angular/core';
import { UnauthorizedError, UnauthorizedErrorSource } from '../models/api.model';

/**
 * Service responsible for managing global authorization error state.
 *
 * This service follows the element-scoped signal-based state management pattern:
 * - Private writable signals for internal state
 * - Public readonly signals for consumers
 * - Computed signals for derived values
 * - Mutation methods for state updates
 *
 * @example
 * ```typescript
 * // In component providers array (element-scoped)
 * @Component({ providers: [ErrorStateService] })
 *
 * // Inject and use
 * const errorState = inject(ErrorStateService);
 *
 * // Check authorization state (reactive)
 * if (errorState.isUnauthorized()) {
 *   // Show unauthorized screen
 * }
 *
 * // Set error (from interceptor)
 * errorState.setUnauthorized({
 *   status: 403,
 *   source: 'fga',
 *   message: 'Access denied',
 *   timestamp: Date.now()
 * });
 *
 * // Clear error (on retry)
 * errorState.clearUnauthorized();
 * ```
 */
@Injectable() // NO providedIn - element-scoped (guides/02-signal-state-web-components.md)
export class ErrorStateService {
  // ---------------------------------------------------------------------------
  // Private Writable Signals
  // ---------------------------------------------------------------------------

  /**
   * Internal writable signal for unauthorized state flag
   */
  private readonly _isUnauthorized = signal(false);

  /**
   * Internal writable signal for error details
   */
  private readonly _errorDetails = signal<UnauthorizedError | null>(null);

  // ---------------------------------------------------------------------------
  // Public Readonly Signals
  // ---------------------------------------------------------------------------

  /**
   * Readonly signal indicating if an authorization error has occurred.
   * When true, the UI should display the unauthorized screen.
   */
  readonly isUnauthorized = this._isUnauthorized.asReadonly();

  /**
   * Readonly signal exposing the current error details.
   * Null if no authorization error has occurred.
   */
  readonly errorDetails = this._errorDetails.asReadonly();

  // ---------------------------------------------------------------------------
  // Computed Signals
  // ---------------------------------------------------------------------------

  /**
   * Computed signal indicating if the error is specifically a 403 Forbidden.
   * Useful for distinguishing between "not authenticated" (401) and "not authorized" (403).
   */
  readonly isForbidden = computed(() => this._errorDetails()?.status === 403);

  /**
   * Computed signal returning the error source.
   * Returns null if no error has occurred.
   */
  readonly errorSource = computed<UnauthorizedErrorSource | null>(
    () => this._errorDetails()?.source ?? null
  );

  /**
   * Computed signal returning the error message.
   * Returns null if no error has occurred.
   */
  readonly errorMessage = computed(() => this._errorDetails()?.message ?? null);

  // ---------------------------------------------------------------------------
  // Mutation Methods
  // ---------------------------------------------------------------------------

  /**
   * Sets the unauthorized error state.
   * Called by the error interceptor when a 401/403 response is received.
   *
   * @param error - The unauthorized error details
   */
  setUnauthorized(error: UnauthorizedError): void {
    this._errorDetails.set(error);
    this._isUnauthorized.set(true);
  }

  /**
   * Clears the unauthorized error state.
   * Called when the user retries or the error is resolved.
   */
  clearUnauthorized(): void {
    this._errorDetails.set(null);
    this._isUnauthorized.set(false);
  }

  /**
   * Resets all state to initial values.
   * Useful for component cleanup or re-initialization.
   */
  reset(): void {
    this._isUnauthorized.set(false);
    this._errorDetails.set(null);
  }
}
